import { HttpClient, HttpResponse } from "@angular/common/http";
import { FilePayload } from "../models/filepayload"
import { Injectable } from "@angular/core";
import { environment } from '../../environments/environment';
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Campaign } from "../models/campaign";
import { Edit } from "../models/campaignFile";
import { GoogleDriveFile } from "../models/googleDriveFile";
import { UploadResponse } from "../models/uploadResponse";

@Injectable({ providedIn: 'root' })
export class FileService {
  constructor(public http: HttpClient) {}

  deleteGoogleDriveFile(fileId: string) {
    const url = `${environment.apiUrl}files/${fileId}`;
    return this.http.delete(url);
  }

  getUploadUrl(campaignId: string, fileName: string, contentType: string, fileSize: number): Observable<UploadResponse> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/upload-url`;

    const body = {
      fileName: fileName,
      contentType: contentType,
      fileSize: fileSize
    };

    return this.http.post<UploadResponse>(url, body);
  }

  GetFileEdits(fileId: string): Observable<Edit[]> {
    const url = `${environment.apiUrl}files/${fileId}/edits`;
    return this.http.get<Edit[]>(url);
  }

  GetGoogleDriveFiles(campaignId: string): Observable<GoogleDriveFile[]> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/files`;
    return this.http.get<GoogleDriveFile[]>(url);
  }

  AnalyzeContract(campaignId: string, fileId: string): Observable<any> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/files/${fileId}/analyzeContract`;
    return this.http.post(url, {});
  }

  GetAnalyzeContractStatus(campaignId: string, fileId: string): Observable<{ analyzed: boolean }> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/files/${fileId}/analyzeContract/status`;
    return this.http.get<{ analyzed: boolean }>(url);
  }

  UploadFea(campaignId: string, file: File): Observable<{ success: boolean; campaignId: string; fea: string; processingStarted: boolean; processingError?: string }> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/fea`;
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<any>(url, formData);
  }

  DeleteFea(campaignId: string): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/fea`;
    return this.http.delete<void>(url);
  }

  uploadFileToGoogleDrive(uploadUrl: string, file: File, chunkSize?: number): Observable<any> {
    const defaultChunkSize = chunkSize || 256 * 1024; // 256KB default chunk size
    
    // For small files, upload in one request
    if (file.size <= defaultChunkSize) {
      return this.uploadSingleChunk(uploadUrl, file, 0, file.size - 1, file.size);
    }
    
    // For large files, use resumable upload
    return this.uploadFileInChunks(uploadUrl, file, defaultChunkSize);
  }

  private uploadSingleChunk(uploadUrl: string, file: File, start: number, end: number, totalSize: number): Observable<any> {
    const chunk = file.slice(start, end + 1);
    
    return this.http.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': file.type,
        'Content-Range': `bytes ${start}-${end}/${totalSize}`
      },
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<any>) => {
        // Handle different response types
        if (response.status === 200 || response.status === 201) {
          // Upload complete - response body contains file metadata (JSON)
          try {
            return typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
          } catch {
            return response.body; // Return as-is if not JSON
          }
        } else if (response.status === 308) {
          // Partial upload - need to continue
          return { status: 'partial', range: response.headers.get('Range') };
        }
        return response;
      })
    );
  }

  private uploadFileInChunks(uploadUrl: string, file: File, chunkSize: number): Observable<any> {
    return new Observable(observer => {
      let start = 0;
      
      const uploadNextChunk = () => {
        if (start >= file.size) {
          observer.complete();
          return;
        }
        
        const end = Math.min(start + chunkSize - 1, file.size - 1);
        
        this.uploadSingleChunk(uploadUrl, file, start, end, file.size).subscribe({
          next: (response) => {
            if (response.status === 'partial') {
              // Continue with next chunk
              start = end + 1;
              uploadNextChunk();
            } else {
              // Upload complete
              observer.next(response);
              observer.complete();
            }
          },
          error: (error) => {
            if (error.status === 308) {
              // Resumable upload - get the range and continue
              const range = error.headers?.get('Range');
              if (range) {
                const matches = range.match(/bytes=0-(\d+)/);
                if (matches) {
                  start = parseInt(matches[1]) + 1;
                  uploadNextChunk();
                  return;
                }
              }
            }
            observer.error(error);
          }
        });
      };
      
      uploadNextChunk();
    });
  }

}
